import React from "react";

/**
 * Root error boundary. Without this, any error thrown while the app boots
 * (render, a provider, an effect) is swallowed and React leaves an empty
 * #root — the page shows only the body's background colour, with no spinner
 * and no content, which is impossible to diagnose from the outside.
 *
 * Styling is deliberately inline (no MUI/theme dependency) so the fallback
 * still renders even when the crash is in the theme or a shared component.
 */
interface State {
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface it in the console too, for anyone with dev tools open.
    // eslint-disable-next-line no-console
    console.error("App crashed:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          fontFamily: "system-ui, -apple-system, sans-serif",
          color: "#2A231C",
          background: "#FAF3E9",
        }}
      >
        <div style={{ maxWidth: 520, textAlign: "center" }}>
          <h1 style={{ fontSize: "1.4rem", marginBottom: 12 }}>Er ging iets mis</h1>
          <p style={{ color: "#6E6253", marginBottom: 20, lineHeight: 1.5 }}>
            De pagina kon niet volledig laden. Probeer te vernieuwen. Blijft dit gebeuren,
            stuur dan onderstaande foutmelding door.
          </p>
          <pre
            style={{
              textAlign: "left",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: ".8rem",
              background: "#fff",
              border: "1px solid #E7D9C5",
              borderRadius: 10,
              padding: 12,
              color: "#8C1E2B",
              marginBottom: 20,
            }}
          >
            {error.name}: {error.message}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              border: "none",
              borderRadius: 9999,
              padding: "12px 24px",
              minHeight: 48,
              fontSize: "1rem",
              fontWeight: 600,
              color: "#fff",
              background: "#C8381E",
              cursor: "pointer",
            }}
          >
            Opnieuw laden
          </button>
        </div>
      </div>
    );
  }
}
